import { useNavigate } from 'react-router-dom'
import { useEffect, useRef, useState } from 'react'
import { SPECIES_MODELS } from '../config/speciesModels'

const COLOR_GREEN = '#a8f446'
const COLOR_VERIFIED = '#1e00ff' // azul: árbol depurado / verificado=true
const COLOR_REVIEW = '#f59e0b' // naranja: revisado por supervisor, requiere correcciones
const COLOR_CORRECTED = '#14b8a6' // teal: en revisión con corrección aplicada (espera cierre)
const COLOR_UNVERIFIED = '#DC143C' // carmesí: no verificado
const TRANSITION_ZOOM = 17 // Zoom donde empiezan los 3D
const TRANSITION_END_ZOOM = 19 // Zoom donde los puntos desaparecen completamente
const CLUSTER_MAX_ZOOM = TRANSITION_ZOOM - 1 // Último zoom con clusters
export const TREE_CODE_LABEL_LAYER_ID = 'tree-code-labels'
// Primer zoom sin clusters: supercluster agrupa por zoom entero hasta CLUSTER_MAX_ZOOM.
// Entero a propósito: uno fraccionario rotularía solo los puntos aislados entre clusters.
const TREE_CODE_LABEL_MIN_ZOOM = CLUSTER_MAX_ZOOM + 1

// Opacidad por zoom de los puntos (fade gradual). Se exporta para que
// usePointsOnlyMode pueda restaurarla al desactivar el modo de solo-puntos.
export const POINT_OPACITY_BY_ZOOM = [
  'interpolate',
  ['linear'],
  ['zoom'],
  TRANSITION_ZOOM,
  0.95,
  TRANSITION_ZOOM + 0.5,
  0.65,
  TRANSITION_ZOOM + 1,
  0.35,
  TRANSITION_END_ZOOM,
  0.1,
]

// Rampas de la etiqueta de código; desde z20 conservan los valores previos. Stops en
// zooms absolutos (como circle-radius): siguen siendo válidos si cambia
// TREE_CODE_LABEL_MIN_ZOOM, porque interpolate exige stops estrictamente crecientes.
// text-offset es de layout y se evalúa con el zoom entero del tile, por eso lleva un
// stop por zoom hasta z20 (deja el texto 2-4 px bajo el círculo del punto).
const TREE_CODE_LABEL_SIZE_BY_ZOOM = [
  'interpolate',
  ['linear'],
  ['zoom'],
  17,
  10,
  20,
  10.75,
  21,
  14.5,
  22,
  15.25,
  24,
  15.5,
]

const TREE_CODE_LABEL_OFFSET_BY_ZOOM = [
  'interpolate',
  ['linear'],
  ['zoom'],
  17,
  ['literal', [0, 0.75]],
  18,
  ['literal', [0, 0.8]],
  19,
  ['literal', [0, 0.85]],
  20,
  ['literal', [0, 1.45]],
  21,
  ['literal', [0, 1.7]],
  22,
  ['literal', [0, 1.9]],
  24,
  ['literal', [0, 2.1]],
]

const TREE_CODE_LABEL_OPACITY_BY_ZOOM = [
  'interpolate',
  ['linear'],
  ['zoom'],
  17,
  0.85,
  20,
  0.95,
  22,
  1,
]

export const useConfigureMapLayers = ({
  map,
  canCompareVerified,
  pathname,
  dispatch,
  setPropsContent,
  setPopupLngLat,
  glbUrl = '/models/tree.glb',
  selectTree,
}) => {
  const navigate = useNavigate()
  const setupCompleteRef = useRef(false)
  const [layersReady, setLayersReady] = useState(false)

  useEffect(() => {
    if (!map) return

    let cancelled = false
    let retryTimer = null
    let styleLoadHandler = null

    const hasStyle = () => {
      try {
        return Boolean(map.getStyle?.())
      } catch {
        return false
      }
    }

    const isStyleReady = () => {
      try {
        return Boolean(map.getStyle?.()) && map.isStyleLoaded()
      } catch {
        return false
      }
    }

    if (setupCompleteRef.current) {
      if (!hasStyle()) {
        setupCompleteRef.current = false
      } else {
        const hasSource = map.getSource('trees')
        const hasClusters = map.getLayer('clusters')
        const hasCodeLabels = map.getLayer(TREE_CODE_LABEL_LAYER_ID)
        const hasDefaultLayer = map.getLayer('trees-3d-default')

        // Verificar que al menos exista el layer default
        const hasAll = hasSource && hasClusters && hasCodeLabels && hasDefaultLayer

        if (hasAll) {
          setLayersReady(true)
          return
        }
        setupCompleteRef.current = false
      }
    }

    const setupLayers = () => {
      if (cancelled || !isStyleReady()) return

      try {
        try {
          map.setConfigProperty('basemap', 'showPointOfInterestLabels', false)
        } catch {
          // Ignorar si el estilo activo no soporta esta propiedad (ej: satellite)
        }

        // Fuente
        if (!map.getSource('trees')) {
          map.addSource('trees', {
            type: 'geojson',
            data: { type: 'FeatureCollection', features: [] },
            cluster: true,
            clusterMaxZoom: CLUSTER_MAX_ZOOM,
            clusterRadius: 40,
          })
        }

        const layers = map.getStyle()?.layers || []
        const labelLayerId = layers.find(
          l => l.type === 'symbol' && l.layout?.['text-field'],
        )?.id

        // Clusters (sin cambios)
        if (!map.getLayer('clusters')) {
          map.addLayer({
            id: 'clusters',
            type: 'circle',
            slot: 'top',
            source: 'trees',
            filter: ['has', 'point_count'],
            maxzoom: TRANSITION_ZOOM,
            paint: {
              'circle-color': [
                'step',
                ['get', 'point_count'],
                COLOR_GREEN,
                100,
                COLOR_GREEN,
                750,
                COLOR_GREEN,
              ],
              'circle-radius': ['step', ['get', 'point_count'], 20, 100, 30, 750, 40],
              'circle-stroke-width': 2,
              'circle-stroke-color': '#ffffff',
              'circle-emissive-strength': 1,
            },
          })
        }

        if (!map.getLayer('cluster-count')) {
          map.addLayer({
            id: 'cluster-count',
            type: 'symbol',
            slot: 'top',
            source: 'trees',
            filter: ['has', 'point_count'],
            maxzoom: TRANSITION_ZOOM,
            layout: {
              'text-field': ['get', 'point_count_abbreviated'],
              'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
              'text-size': 13,
              'text-allow-overlap': true,
              'text-ignore-placement': true,
            },
            paint: {
              'text-color': '#000000',
              'text-emissive-strength': 1,
            },
          })
        }

        // 🎯 Puntos con TRANSICIÓN GRADUAL
        if (!map.getLayer('unclustered-point')) {
          map.addLayer({
            id: 'unclustered-point',
            type: 'circle',
            slot: 'top',
            source: 'trees',
            filter: ['!', ['has', 'point_count']],
            minzoom: 8,
            maxzoom: 24, // ✅ Ahora visibles hasta el final
            paint: {
              'circle-radius': {
                base: 6,
                stops: [
                  [8, 4],
                  [10, 4.5],
                  [12, 5],
                  [16, 4.6],
                  [TRANSITION_ZOOM, 4.3],
                  [TRANSITION_END_ZOOM, 4],
                  [21, 10],
                  [22, 20],
                ],
              },
              // Fade gradual del punto (se restaura desde usePointsOnlyMode).
              // En modo solo-puntos pasa a 1 (100%) para comparar verificados.
              'circle-opacity': POINT_OPACITY_BY_ZOOM,
              'circle-stroke-width': 1,
              'circle-color': COLOR_GREEN,
              'circle-stroke-color': '#ffffff',
              // 🎨 Stroke opacity se mantiene visible
              // ⚠️ MODIFICADO: Antes se desvanecía hasta 0.75, ahora se mantiene en 1
              'circle-stroke-opacity': [
                'interpolate',
                ['linear'],
                ['zoom'],
                TRANSITION_ZOOM,
                1,
                TRANSITION_END_ZOOM,
                1, // Se mantiene visible para mejor clickeabilidad
              ],
              'circle-emissive-strength': 1,
            },
          })
        }

        if (!map.getLayer(TREE_CODE_LABEL_LAYER_ID)) {
          map.addLayer(
            {
              id: TREE_CODE_LABEL_LAYER_ID,
              type: 'symbol',
              slot: 'top',
              source: 'trees',
              filter: ['all', ['!', ['has', 'point_count']], ['has', 'code']],
              minzoom: TREE_CODE_LABEL_MIN_ZOOM,
              layout: {
                // Oculta por defecto: la enciende useTreeCodeLabels según el control.
                visibility: 'none',
                'text-field': ['to-string', ['get', 'code']],
                'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
                'text-size': TREE_CODE_LABEL_SIZE_BY_ZOOM,
                'text-anchor': 'top',
                'text-offset': TREE_CODE_LABEL_OFFSET_BY_ZOOM,
                'text-allow-overlap': false,
                'text-ignore-placement': false,
                'text-padding': 1,
              },
              paint: {
                'text-color': '#1f2937',
                'text-halo-color': '#ffffff',
                'text-halo-width': 0.7,
                'text-halo-blur': 0.15,
                'text-opacity': TREE_CODE_LABEL_OPACITY_BY_ZOOM,
                'text-emissive-strength': 0.8,
              },
            },
            labelLayerId,
          )
        }

        // Árbol seleccionado (se mantiene visible)
        if (!map.getLayer('selected-tree-style')) {
          map.addLayer({
            id: 'selected-tree-style',
            type: 'circle',
            slot: 'top',
            source: 'trees',
            filter: ['==', ['get', 'id'], ''],
            minzoom: 8,
            maxzoom: TRANSITION_ZOOM,
            paint: {
              'circle-color': '#ffffff',
              'circle-radius': 8,
              'circle-stroke-width': 3,
              'circle-stroke-color': '#ff0000',
              'circle-emissive-strength': 1,
            },
          })
        }

        // Modelos 3D - Registro
        if (!map.hasModel?.('tree-default')) {
          map.addModel('tree-default', glbUrl)
        }

        Object.values(SPECIES_MODELS).forEach(({ id, url }) => {
          if (!map.hasModel?.(id)) {
            map.addModel(id, url)
          }
        })

        // 🎨 Función para generar paint con factor de escala específico
        const createModelPaint = (scaleFactor = 1.0) => ({
          'model-scale': [
            // X (ancho) - basado en DAP
            [
              '*',
              scaleFactor,
              [
                'case',
                ['has', 'dap'],
                [
                  'interpolate',
                  ['linear'],
                  ['to-number', ['get', 'dap']],
                  0,
                  0.3,
                  5,
                  0.5,
                  10,
                  0.7,
                  20,
                  0.9,
                  30,
                  1.1,
                  40,
                  1.3,
                  50,
                  1.5,
                  60,
                  1.8,
                  80,
                  2.2,
                  100,
                  2.6,
                ],
                1.0,
              ],
            ],
            // Y (profundidad) - basado en DAP también
            [
              '*',
              scaleFactor,
              [
                'case',
                ['has', 'dap'],
                [
                  'interpolate',
                  ['linear'],
                  ['to-number', ['get', 'dap']],
                  0,
                  0.3,
                  5,
                  0.5,
                  10,
                  0.7,
                  20,
                  0.9,
                  30,
                  1.1,
                  40,
                  1.3,
                  50,
                  1.5,
                  60,
                  1.8,
                  80,
                  2.2,
                  100,
                  2.6,
                ],
                1.0,
              ],
            ],
            // Z (altura) - basado en HT
            [
              '*',
              scaleFactor,
              [
                'case',
                ['has', 'ht'],
                [
                  'interpolate',
                  ['linear'],
                  ['to-number', ['get', 'ht']],
                  0,
                  0.3,
                  2,
                  0.5,
                  4,
                  0.7,
                  6,
                  0.9,
                  8,
                  1.1,
                  10,
                  1.3,
                  15,
                  1.6,
                  20,
                  2.0,
                  25,
                  2.4,
                  30,
                  2.8,
                ],
                1.0,
              ],
            ],
          ],
          'model-color': COLOR_GREEN,
          'model-emissive-strength': 1,
          'model-rotation': [0, 0, ['get', 'randomRotation']],
          'model-opacity': [
            'interpolate',
            ['linear'],
            ['zoom'],
            TRANSITION_ZOOM,
            0.4,
            TRANSITION_ZOOM + 0.5,
            0.8,
            TRANSITION_ZOOM + 1,
            1,
          ],
        })

        // 🎯 Layers 3D por especie
        Object.entries(SPECIES_MODELS).forEach(([specieName, model]) => {
          const layerId = `trees-3d-${model.id}`

          if (!map.getLayer(layerId)) {
            map.addLayer(
              {
                id: layerId,
                type: 'model',
                slot: 'top',
                source: 'trees',
                minzoom: TRANSITION_ZOOM,
                filter: [
                  'all',
                  ['!', ['has', 'point_count']],
                  ['==', ['get', 'specie', ['get', 'tooltip']], specieName],
                ],
                layout: {
                  'model-id': model.id,
                },
                paint: createModelPaint(model.scaleFactor),
              },
              labelLayerId,
            )
          }
        })

        // 🌲 Layer 3D por defecto (resto de especies)
        if (!map.getLayer('trees-3d-default')) {
          map.addLayer(
            {
              id: 'trees-3d-default',
              type: 'model',
              slot: 'top',
              source: 'trees',
              minzoom: TRANSITION_ZOOM,
              filter: [
                'all',
                ['!', ['has', 'point_count']],
                [
                  '!',
                  [
                    'in',
                    ['get', 'specie', ['get', 'tooltip']],
                    ['literal', Object.keys(SPECIES_MODELS)],
                  ],
                ],
              ],
              layout: {
                'model-id': 'tree-default',
              },
              paint: createModelPaint(1.0),
            },
            labelLayerId,
          )
        }

        setupCompleteRef.current = true
        setLayersReady(true)
      } catch (error) {
        console.error('[useConfigureMapLayers] Error:', error)
      }
    }

    const waitForStyleAndSetup = () => {
      let attempts = 0
      const maxAttempts = 50

      const checkAndSetup = () => {
        if (cancelled) return

        if (isStyleReady()) {
          setupLayers()
        } else if (attempts < maxAttempts) {
          attempts++
          retryTimer = setTimeout(checkAndSetup, 100)
        }
      }

      checkAndSetup()
    }

    // Al entrar al efecto, asumir no listo hasta validar la presencia real de capas.
    setLayersReady(false)
    waitForStyleAndSetup()

    styleLoadHandler = () => {
      if (cancelled) return
      setupCompleteRef.current = false
      setLayersReady(false)
      waitForStyleAndSetup()
    }
    map.on('style.load', styleLoadHandler)

    return () => {
      cancelled = true
      if (styleLoadHandler) {
        map.off('style.load', styleLoadHandler)
      }
      if (retryTimer) {
        clearTimeout(retryTimer)
      }
    }
  }, [map, glbUrl])

  // Estilos
  useEffect(() => {
    if (!map || !layersReady) return

    const applyPointColor = () => {
      try {
        // Color del punto (independiente del anillo)
        if (map.getLayer('unclustered-point')) {
          map.setPaintProperty(
            'unclustered-point',
            'circle-color',
            canCompareVerified
              ? [
                  'case',
                  [
                    'all',
                    ['==', ['get', 'estadoRegistro'], 'en_revision'],
                    ['==', ['get', 'correccionAplicada'], true],
                  ],
                  COLOR_CORRECTED,
                  ['==', ['get', 'estadoRegistro'], 'en_revision'],
                  COLOR_REVIEW,
                  ['==', ['get', 'estadoRegistro'], 'verificado'],
                  COLOR_VERIFIED,
                  ['==', ['get', 'estadoRegistro'], 'no_verificado'],
                  COLOR_UNVERIFIED,
                  ['boolean', ['get', 'treeVerified'], false],
                  COLOR_VERIFIED,
                  COLOR_UNVERIFIED,
                ]
              : COLOR_GREEN,
          )
        }
      } catch (error) {
        console.error('[useConfigureMapLayers] Error estilos:', error)
      }
    }

    // Si el estilo aún no está listo (típico en recarga dura, cuando la
    // autenticación se resuelve antes de que cargue el mapa), reaplicar el
    // color al próximo idle en lugar de descartarlo en silencio.
    if (map.isStyleLoaded?.()) {
      applyPointColor()
    } else {
      map.once('idle', applyPointColor)
    }

    return () => {
      map.off('idle', applyPointColor)
    }
  }, [map, layersReady, canCompareVerified, pathname])

  // Eventos
  useEffect(() => {
    if (!map || !layersReady) return

    const handleClusterClick = e => {
      const features = map.queryRenderedFeatures(e.point, { layers: ['clusters'] })
      if (!features.length) return
      const clusterId = features[0].properties.cluster_id
      map.getSource('trees').getClusterExpansionZoom(clusterId, (err, zoom) => {
        if (err) return
        map.easeTo({ center: features[0].geometry.coordinates, zoom, duration: 500 })
      })
    }

    const handleUnclusteredPointClick = e => {
      if (!e.features?.length || !selectTree) return
      const feature = e.features[0]
      const coordinates = feature.geometry.coordinates.slice()
      selectTree(feature, coordinates, 'click')
    }

    // ⚠️ DESACTIVADO: Click en modelos 3D (para mejor precisión de selección)
    // Solo se permite selección en la base del árbol (unclustered-point)
    // Para revertir: descomentar este handler y los eventos más abajo
    // const handleModelClick = e => {
    //   if (!e.features?.length || !selectTree) return
    //   const feature = e.features[0]
    //   const coordinates = feature.geometry.coordinates.slice()
    //   selectTree(feature, coordinates, 'click')
    // }

    const setCursorPointer = () => {
      map.getCanvas().style.cursor = 'pointer'
    }
    const setCursorDefault = () => {
      map.getCanvas().style.cursor = ''
    }

    // ⚠️ DESACTIVADO: Array de todos los layers 3D (para eventos de click)
    // Para revertir: descomentar estas líneas
    // const all3DLayers = [
    //   'trees-3d-default',
    //   ...Object.values(SPECIES_MODELS).map(model => `trees-3d-${model.id}`),
    // ]

    // Eventos para layers estáticos
    map.on('click', 'clusters', handleClusterClick)
    map.on('click', 'unclustered-point', handleUnclusteredPointClick)
    map.on('mouseenter', 'clusters', setCursorPointer)
    map.on('mouseenter', 'unclustered-point', setCursorPointer)
    map.on('mouseleave', 'clusters', setCursorDefault)
    map.on('mouseleave', 'unclustered-point', setCursorDefault)

    // ⚠️ DESACTIVADO: Eventos para todos los layers 3D
    // Para revertir: descomentar estas líneas
    // all3DLayers.forEach(layerId => {
    //   map.on('click', layerId, handleModelClick)
    //   map.on('mouseenter', layerId, setCursorPointer)
    //   map.on('mouseleave', layerId, setCursorDefault)
    // })

    return () => {
      map.off('click', 'clusters', handleClusterClick)
      map.off('click', 'unclustered-point', handleUnclusteredPointClick)
      map.off('mouseenter', 'clusters', setCursorPointer)
      map.off('mouseenter', 'unclustered-point', setCursorPointer)
      map.off('mouseleave', 'clusters', setCursorDefault)
      map.off('mouseleave', 'unclustered-point', setCursorDefault)

      // ⚠️ DESACTIVADO: Cleanup de eventos 3D
      // Para revertir: descomentar estas líneas
      // all3DLayers.forEach(layerId => {
      //   map.off('click', layerId, handleModelClick)
      //   map.off('mouseenter', layerId, setCursorPointer)
      //   map.off('mouseleave', layerId, setCursorDefault)
      // })
    }
  }, [
    map,
    layersReady,
    dispatch,
    setPropsContent,
    setPopupLngLat,
    pathname,
    navigate,
    selectTree,
  ])

  return { layersReady }
}

export default useConfigureMapLayers
